import { SectionContainer } from "../molecules/section-container";
import { MediaCapture } from "../molecules/media-capture";
import { useEffect, useState } from "react";
import { ReviewSubmission } from "../molecules/review-submission";
import { AddMetadata } from "../molecules/add-metadata";
import { SubmissionSuccessful } from "../molecules/submission-successful";
import { useTasks } from "../../providers/task-provider";
import { useSubmission } from "../../providers/submission-provider";
import { useAuth } from "../../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { UploadingSubmission } from "../molecules/uploading-submission";
import { SubmissionFailed } from "../molecules/submission-failed";
import {useBackendUser} from "../../providers/user-provider";
import {useDemographies} from "../../providers/demographies-provider";

const StateEnmun = {
  ASSET_CAPTURE: "ASSET_TAKEN",
  ADD_METADATA: "ADD_METADATA",
  REVIEW_SUBMISSION: "REVIEW_SUBMISSION",
  SUBMITTED: "SUBMITTED",
  SUBMISSION_ERROR: "SUBMISSION_ERROR",
  SUBMITTING: "SUBMITTING",
};

const apiUrl = process.env.REACT_APP_API_URL;

export const RecordingPage = () => {
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [currentState, setCurrentState] = useState<string>(
    StateEnmun.ASSET_CAPTURE,
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [categoryContentsText, setCategoryContentsText] = useState<string>("");
  const { currentTask } = useTasks();
  const { submission, setSubmissionProperties, initializeSubmission, setSubmissionMetadata } =
    useSubmission();
  const auth = useAuth() as { id: string | null; user: any } | null;
  const navigate = useNavigate();
  const {backendUser} = useBackendUser()
  const {demosResponse} = useDemographies()

  useEffect(() => {
    if (currentTask) {
      if (!submission) {
        initializeSubmission({
          category_id: currentTask?.id,
          device_info: {},
          location_info: null,
          submission_metadata: {
            sound_type: currentTask?.name,
          },
        });
      }
      // Fetch category contents and concatenate into a single string
      const fetchCategoryContents = async () => {
        try {
          if (!apiUrl || !currentTask?.id) return;
          const url = `${apiUrl}/category/${currentTask.id}/contents`;
          const resp = await fetch(
            url,
            {
              headers: {
                Authorization: `bearer ${auth?.user?.accessToken}`,
              },
            },
          );
          if (!resp.ok) {
            console.warn("Failed to fetch category contents", resp.status);
            return;
          }
          const result = await resp.json();
          const texts = Array.isArray(result)
            ? result.map((item: any) =>
                typeof item === "string" ? item : (item?.content ?? "")
              )
            : [];

          setCategoryContentsText(texts.join(" "));
        } catch (e) {
          console.error("Failed to load category contents", e);
        }
      };
      fetchCategoryContents().then()
      backendUser?.demography_ids?.forEach(demographyId => {
        const demography = demosResponse.find(demography => demography.id === demographyId)
        setSubmissionMetadata({audio_quality: '44.1 kHz' })
        if (!demography) return
        setSubmissionMetadata({[demography.type]: demography.name })
      })
    } else {
      navigate("/");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!submission?.user_id) {
      if (auth?.id as string) {
        setSubmissionProperties({ user_id: auth?.id });
      } else if (auth?.user) {
        const userUrl = `${apiUrl}/users`;
        const apiResponseAsync = fetch(
          `${userUrl}/${auth?.user?.email as string}/`,
          {
            headers: {
              Authorization: `bearer ${auth?.user?.accessToken}`,
            },
          },
        );
        apiResponseAsync.then((apiResponse) => {
          if (!apiResponse.ok) {
            let errorDetail =
              "Failed to retrieve user information from the database.";
            apiResponse
              .json()
              .then((errorData) => {
                errorDetail = errorData.detail || errorDetail;
                throw new Error(errorDetail);
              })
              .catch((jsonError) => {
                console.error(
                  "Error parsing backend error response:",
                  jsonError,
                );
                throw new Error(errorDetail);
              });
          }
          apiResponse.json().then((dbUser) => {
            setSubmissionProperties({ user_id: dbUser?.id });
          });
        });
      }
    }
  }, [auth, submission]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!submission || !audioFile) {
      console.error("Submission data, audio file, or picture file is missing.");
      setCurrentState(StateEnmun.SUBMISSION_ERROR); // Or handle error appropriately
      return;
    }

    setCurrentState(StateEnmun.SUBMITTING);

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      console.error("REACT_APP_API_URL is not defined");
      setCurrentState(StateEnmun.SUBMISSION_ERROR);
      return;
    }

    const files = [
      {
        file_key: `${submission.id}/${audioFile.name}`,
        file_type: audioFile.type,
      },
      {
        file_key: `${submission.id}/metadata.json`,
        file_type: "application/json",
      },
    ];

    try {
      const userUrl = `${apiUrl}/users`;
      const apiResponse = await fetch(
        `${userUrl}/${auth?.user?.email as string}/available-for-submission/`,
        {
          headers: {
            Authorization: `bearer ${auth?.user?.accessToken}`,
          },
        },
      );

      if (!apiResponse.ok) {
        throw new Error(
          `Failed to get user information: ${apiResponse.statusText}`,
        );
      }

      const { available_for_submission } = await apiResponse.json();

      if (!available_for_submission) {
        throw new Error(
          "You've reached your maximum submission amount. We're reviewing your work right now. Stay tuned for most opportunities.",
        );
      }

      const response = await fetch(`${apiUrl}/assets/upload-urls/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${auth?.user?.accessToken}`,
        },
        body: JSON.stringify(files),
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URLs: ${response.statusText}`);
      }

      const { urls } = await response.json();

      if (!urls || urls.length < 2) {
        throw new Error("Did not receive the expected number of upload URLs.");
      }

      const [audioUploadUrl, metadataUploadUrl] = urls;

      // Upload audio file
      const audioUploadResponse = await fetch(audioUploadUrl, {
        method: "PUT",
        body: audioFile,
        headers: {
          "Content-Type": audioFile.type,
        },
      });
      if (!audioUploadResponse.ok) {
        throw new Error(
          `Failed to upload audio file: ${audioUploadResponse.statusText}`,
        );
      }

      // Upload metadata
      const metadataBlob = new Blob(
        [JSON.stringify(submission.submission_metadata)],
        { type: "application/json" },
      );
      const metadataUploadResponse = await fetch(metadataUploadUrl, {
        method: "PUT",
        body: metadataBlob,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!metadataUploadResponse.ok) {
        throw new Error(
          `Failed to upload metadata: ${metadataUploadResponse.statusText}`,
        );
      }

      const submissionApiUrl = `${apiUrl}/submission/`;
      const submissionResponse = await fetch(submissionApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${auth?.user?.accessToken}`,
        },
        body: JSON.stringify(submission),
      });

      if (!submissionResponse.ok) {
        throw new Error(
          `Failed to post submission data: ${submissionResponse.statusText}`,
        );
      }

      setCurrentState(StateEnmun.SUBMITTED);
      if (typeof fbq === "function") {
        fbq("track", "Subscribe");
      }
    } catch (error) {
      console.error("Submission failed:", error);
      setErrorMessage((error as any)?.message);
      setCurrentState(StateEnmun.SUBMISSION_ERROR);
    }
  };

  return (
    <>
      {currentState === StateEnmun.ASSET_CAPTURE && (
        <SectionContainer title="Perle Collect - Record Audio">
          <MediaCapture
            content={categoryContentsText}
            audioName={currentTask?.name || "Audio Recording"}
            onMediaTaken={(audioFile) => {
              setAudioFile(audioFile);
              if (!currentTask?.metadata_schema || Object.keys(currentTask.metadata_schema).length === 0) {
                setCurrentState(StateEnmun.REVIEW_SUBMISSION);
              } else {
                setCurrentState(StateEnmun.ADD_METADATA);
              }
            }}
            onGoBack={() => navigate("/")}
          />
        </SectionContainer>
      )}

      {currentState === StateEnmun.ADD_METADATA && (
        <SectionContainer title="Perle Collect - Add Metadata">
          <AddMetadata
            audioFile={audioFile!}
            onAddMetadata={() => setCurrentState(StateEnmun.REVIEW_SUBMISSION)}
            onBack={() => {
              setCurrentState(StateEnmun.ASSET_CAPTURE);
              setAudioFile(null);
            }}
            soundType={currentTask?.name || "Audio Recording"}
          />
        </SectionContainer>
      )}

      {currentState === StateEnmun.REVIEW_SUBMISSION && (
        <SectionContainer title="Perle Collect - Review Recording">
          <ReviewSubmission
            audioFile={audioFile!}
            onEdit={() => {
                if (!currentTask?.metadata_schema || Object.keys(currentTask.metadata_schema).length === 0) {
                  setCurrentState(StateEnmun.ASSET_CAPTURE);
                } else {
                  setCurrentState(StateEnmun.ADD_METADATA);
                }
              }
            }
            onSubmit={handleSubmit}
          />
        </SectionContainer>
      )}

      {currentState === StateEnmun.SUBMITTING && (
        <SectionContainer title="Perle Collect - Submitting Recording">
          <UploadingSubmission />
        </SectionContainer>
      )}

      {currentState === StateEnmun.SUBMISSION_ERROR && (
        <SectionContainer title="Perle Collect - Submission Failed">
          <SubmissionFailed errorMessage={errorMessage} />
        </SectionContainer>
      )}

      {currentState === StateEnmun.SUBMITTED && (
        <SectionContainer title="Perle Collect">
          <SubmissionSuccessful completionId={"ABD-123456"} payout={"2.5"} />
        </SectionContainer>
      )}
    </>
  );
};
